import React, { useState } from 'react';
import { ArrowLeft, Phone, Mail, MapPin, User } from 'lucide-react';
import { useQuote } from '../context/QuoteContext';
import { saveQuoteContact } from '../services/database';

interface ContactFormProps {
  onNext: () => void;
  onBack: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onNext, onBack }) => {
  const { state, dispatch } = useQuote();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    preferredContact: 'email',
    numberOfQuotes: '3',
    previousVendors: '',
    consentToContact: false,
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setContactInfo(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contactInfo.consentToContact) {
      setError('Please consent to be contacted about your quote request');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (state.workDescription?.id) {
        await saveQuoteContact(state.workDescription.id, contactInfo);
      }
      
      dispatch({ type: 'SET_CONTACT_INFO', payload: contactInfo });
      onNext();
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="animate-fadeIn">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="pl-9 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                required
                value={contactInfo.firstName}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
              value={contactInfo.lastName}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail size={16} className="text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              className="pl-9 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
              value={contactInfo.email}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone size={16} className="text-gray-400" />
            </div>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="pl-9 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
              value={contactInfo.phone}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MapPin size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              id="address"
              name="address"
              className="pl-9 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
              value={contactInfo.address}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
              value={contactInfo.city}
              onChange={handleChange}
            />
          </div>
          
          <div>
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code
            </label>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              required
              value={contactInfo.zipCode}
              onChange={handleChange}
            />
          </div>
        </div>

        <div>
          <label htmlFor="numberOfQuotes" className="block text-sm font-medium text-gray-700 mb-1">
            How many quotes would you like to receive?
          </label>
          <select
            id="numberOfQuotes"
            name="numberOfQuotes"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            value={contactInfo.numberOfQuotes}
            onChange={handleChange}
          >
            <option value="2">2 quotes</option>
            <option value="3">3 quotes</option>
            <option value="4">4 quotes</option>
            <option value="5">5 quotes</option>
          </select>
        </div>

        <div>
          <label htmlFor="previousVendors" className="block text-sm font-medium text-gray-700 mb-1">
            Have you already received quotes from any vendors? If so, please list their names:
          </label>
          <textarea
            id="previousVendors"
            name="previousVendors"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[80px]"
            placeholder="E.g., ABC Contractors, XYZ Services"
            value={contactInfo.previousVendors}
            onChange={handleChange}
          />
        </div>
        
        <div>
          <label htmlFor="preferredContact" className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Contact Method
          </label>
          <select
            id="preferredContact"
            name="preferredContact"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            value={contactInfo.preferredContact}
            onChange={handleChange}
          >
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="text">Text Message</option>
          </select>
        </div>
        
        <div className="flex items-start gap-2">
          <input
            id="consentToContact"
            name="consentToContact"
            type="checkbox"
            className="mt-1 h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
            checked={contactInfo.consentToContact}
            onChange={handleChange}
            required
          />
          <label htmlFor="consentToContact" className="text-sm text-gray-700">
            I consent to be contacted about my quote request. I understand my data will be used in accordance with the Privacy Policy.
          </label>
        </div>
        
        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        <div className="flex flex-col gap-3">
          <button
            type="submit"
            className="w-full bg-black text-white px-4 py-3 rounded-lg font-medium transition-colors duration-300 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? 'Submitting...' : 'Submit Request'}
          </button>
          
          <button 
            type="button"
            onClick={onBack}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm